import React, { useMemo, useRef, useState } from 'react';
import { Layer, Rect, Stage, Text } from 'react-konva';
import { useRMDispatch } from 'redux/hooks';
import { useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';
import { setWellNavigationDetails } from 'redux/reducers/experimentDataReducer';
import { CanvasMapProps, SELECTED_WELL_FILL, WELL_FILL } from './constants';
import { useAutoScrollToWell, useContainerSize } from './hooks';
import { calculateGridConfig, getCanvasLabels } from './utils';
import { updateDropPath } from 'utils/ExperimentUtils';
import { history } from 'utils/Global';
import { DropUrlParameters } from 'components/Experiment/constants';
import './index.scss';

const CanvasMap = (props: CanvasMapProps) => {
  const { plateNumber, plateBarcode, plateRows, plateColumns } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const dispatch = useRMDispatch();
  const [scrollOffset, setScrollOffset] = useState({ left: 0, top: 0 });

  // Generate labels
  const { rowLabels, columnLabels } = useMemo(
    () => getCanvasLabels(plateColumns, plateRows),
    [plateColumns, plateRows],
  );

  // Calculate grid configuration
  const gridConfig = useMemo(
    () =>
      calculateGridConfig(
        containerSize.width,
        containerSize.height,
        plateRows,
        plateColumns,
      ),
    [containerSize.width, containerSize.height, plateRows, plateColumns],
  );

  useAutoScrollToWell({
    gridConfig: gridConfig,
    scrollContainerRef,
    wellNumber: wellNavigationDetails?.wellNum,
    plateRows,
    plateColumns,
  });

  const {
    cellSize,
    wellSize,
    startX,
    startY,
    labelAreaWidth,
    labelAreaHeight,
    viewportWidth,
    viewportHeight,
    contentWidth,
    contentHeight,
  } = gridConfig || {};

  const selectedRow =
    wellNavigationDetails?.wellNum != null
      ? Math.floor((wellNavigationDetails.wellNum - 1) / plateColumns)
      : null;
  const selectedCol =
    wellNavigationDetails?.wellNum != null
      ? (wellNavigationDetails.wellNum - 1) % plateColumns
      : null;

  const handleWellClick = (wellNumber: number) => {
    updateDropPath(history.location.pathname, [
      { index: DropUrlParameters.WELLNUM, value: wellNumber },
    ]);
    dispatch(
      setWellNavigationDetails([{ ...wellNavigationDetails, wellNum: wellNumber }]),
    );
  };
  return (
    <div className="flex flex-col w-full h-full">
      <div className="p-2 font-semibold">
        {plateNumber}, {plateBarcode}
      </div>
      <div ref={containerRef} className="flex-1 w-full relative min-h-0">
        {gridConfig && (
          <>
            <div
              ref={scrollContainerRef}
              className="overflow-auto canvas-map-scroll"
              style={{
                position: 'absolute',
                left: labelAreaWidth ?? 0,
                top: labelAreaHeight ?? 0,
                width: `${
                  (viewportWidth ?? containerSize.width) - (labelAreaWidth ?? 0)
                }px`,
                height: `${
                  (viewportHeight ?? containerSize.height) - (labelAreaHeight ?? 0)
                }px`,
              }}
              onScroll={(event) => {
                const target = event.currentTarget;
                setScrollOffset({ left: target.scrollLeft, top: target.scrollTop });
              }}
            >
              <Stage width={contentWidth ?? 0} height={contentHeight ?? 0}>
                <Layer>
                  {Array.from({ length: plateRows }, (_, row) =>
                    Array.from({ length: plateColumns }, (_, col) => {
                      const x = col * cellSize;
                      const y = row * cellSize;
                      const wellNumber = col + row * plateColumns + 1;
                      const isSelected = wellNavigationDetails?.wellNum === wellNumber;

                      return (
                        <Rect
                          key={`well-${row}-${col}`}
                          x={x}
                          y={y}
                          width={wellSize}
                          height={wellSize}
                          fill={isSelected ? SELECTED_WELL_FILL : WELL_FILL}
                          stroke="black"
                          strokeWidth={1}
                          onClick={() => handleWellClick(wellNumber)}
                        />
                      );
                    }),
                  )}
                </Layer>
              </Stage>
            </div>
            <Stage
              width={viewportWidth ?? containerSize.width}
              height={viewportHeight ?? containerSize.height}
              className="absolute top-0 left-0 pointer-events-none"
            >
              <Layer
                clipX={0}
                clipY={labelAreaHeight ?? 0}
                clipWidth={labelAreaWidth ?? 0}
                clipHeight={
                  (viewportHeight ?? containerSize.height) - (labelAreaHeight ?? 0)
                }
              >
                {rowLabels.map((label, row) => {
                  const wellX = startX;
                  const wellY = startY + row * cellSize - scrollOffset.top;
                  const labelX = wellX - cellSize / 1.5;
                  const labelY = wellY;
                  const isSelected = selectedRow === row;
                  return (
                    <Text
                      key={`row-${row}`}
                      x={labelX}
                      y={labelY}
                      width={cellSize / 2}
                      height={wellSize}
                      text={label}
                      fontSize={wellSize / 2.5}
                      fill={isSelected ? 'blue' : 'black'}
                      align="center"
                      verticalAlign="middle"
                    />
                  );
                })}
              </Layer>

              <Layer
                clipX={labelAreaWidth ?? 0}
                clipY={0}
                clipWidth={(viewportWidth ?? containerSize.width) - (labelAreaWidth ?? 0)}
                clipHeight={labelAreaHeight ?? 0}
              >
                {columnLabels.map((label, col) => {
                  const wellX = startX + col * cellSize - scrollOffset.left;
                  const wellY = startY;
                  const labelX = wellX;
                  const labelY = wellY - cellSize / 1.5;
                  const isSelected = selectedCol === col;
                  return (
                    <Text
                      key={`col-${col}`}
                      x={labelX}
                      y={labelY}
                      width={wellSize}
                      height={cellSize / 2}
                      text={label}
                      fontSize={wellSize / 2.5}
                      fill={isSelected ? 'blue' : 'black'}
                      align="center"
                      verticalAlign="middle"
                    />
                  );
                })}
              </Layer>
            </Stage>
          </>
        )}
      </div>
    </div>
  );
};

export default CanvasMap;
